<template>
  <tr
    v-sync-rows-height:[applySync]
    class="simple-comparison-row"
    :class="{
      'data-no-border': noBorder,
      'data-no-padding-top': noPaddingTop,
      'data-no-padding-bottom': noPaddingBottom,
      [index % 2 === 1 ? 'even-row' : 'odd-row']: true,
      syncscroll: true,
    }"
  >
    <th
        class="simple-comparison-row__label"
        :class="{'no-border': noHighlight, [index % 2 === 1 ? 'even' : 'odd']: true}"
    >
      <template
        v-if="data.value_labels && data.value_labels.length > 0"
        v-for="(label, k) of data.value_labels"
        :key="k"
      >
        <span class="fact-label">{{ucfirst(data.label)}} {{ label }}</span>{{ ' ' }}
      </template>
      <span v-else class="fact-label">{{ ucfirst(data.label) }}</span>
    </th>
    <component
      v-for="(column, i) of data.columns"
      :key="i"
      :is="index === 0 ? 'th' : 'td'"
      class="similar-comparison-data"
      :class="{
        'similar-comparison-data-first': i === 0 && !noHighlight,
        [index % 2 === 1 ? 'even' : 'odd']: true,
      }"
    >
      <slot :column-data="column" :index="i">
        <template
          v-if="Array.isArray(column.value) && column.value.length > 0"
          v-for="(value, i) of column.value" :key="i"
        >
          <span class="fact-value" v-html="formatValue(value)"></span>{{ ' ' }}
        </template>
        <span v-else-if="Array.isArray(column.value) && column.value.length === 0" class="fact-value">&cross;</span>
        <span v-else v-html="formatValue(column.value)" />
      </slot>
    </component>

    <component
      :is="index === 0 ? 'th' : 'td'"
      v-for="(slotIndex, k) of numberProducts - data.columns.length"
      :key="'empty-slot' + k"
      class="similar-comparison-data simple-comaprison-data__empty-slot"
    >
      <slot name="emptySlot" />
    </component>
  </tr>
</template>

<script setup>
import CustomCheckbox from '@/components/core/CustomCheckbox';
import syncRowsHeight from '@/directives/syncRowsHeight';
import { useSimpleComparisonStore } from "~/stores/simpleComparison";
import { usePageStore } from "~/stores/page";
import { computed } from "vue";

const vSyncRowsHeight = syncRowsHeight;

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  applySync: {
    type: Boolean,
    default: false,
  },
  numberProducts: {
    type: Number,
    default: 5,
  },
  numberProductsMobile: {
    type: Number,
    default: 2,
  },
  noBorder: {
    type: Boolean,
    default: false,
  },
  noPaddingTop: {
    type: Boolean,
    default: false,
  },
  noPaddingBottom: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: '',
  },
  index: {
    type: Number,
    default: 0,
  },
  noHighlight: {
    type: Boolean,
    default: false,
  },
})

const { t } = useI18n();

const simpleComparison = useSimpleComparisonStore();
const page = usePageStore();

const decimalsCount = computed(() => {
  return Math.max(...props.data.columns.map((col) => {
    return Math.max(...col.value.map((val) => {
      if (typeof val === 'number') {
        return val.toString().split('.')[1]?.length || 0
      }
      return 0
    }))
  }));
})
function formatValue(value) {
  if (value === null) {
    return '&dash;';
  }
  if (value === 'No' || value === t('generic.no') || value === false) {
    return '&cross;';
  }
  if (value === 'Yes' || value === t('generic.yes') || value === true) {
    return '&check;';
  }

  let formattedValue = value;

  if (typeof formattedValue === 'number') {
    formattedValue = Number(formattedValue).toFixed(decimalsCount.value);
  }

  if (props.data.units) {
    return props.data.units.length < 2 ? `${formattedValue}${props.data.units}` : `${formattedValue} ${props.data.units}`;
  }

  return value;
}

function ucfirst(string) {
  if (!string) {
    return string;
  }

  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}
</script>

<style lang="scss" scoped>
%small-grey-bold {
  font-size: 12px;
  color: #777777;
  font-weight: bold;
}
.simple-comparison-row {
  display: flex;
  align-items: stretch;

  @media (max-width: $break_xs_under) {
    width: 100%;
    overflow: hidden;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  .current-product-label {
    display: block;
    @extend %small-grey-bold;
  }

  .similar-comparison-data {
    position: relative;
    display: flex;
    flex-direction: column;

    .similar-comparison-data-header {
      font-weight: bold;
      height: 23px;
      white-space: nowrap;
    }
    .similar-msg-container {
      min-height: 45px;
      margin: 30px 0;
      .similar-msg {
        @extend %small-grey-bold;
      }
    }
    :deep() {
      .star-rating {
        position: relative;
        top: 1px;
      }
    }
  }

  .simple-comparison-row__label,
  .similar-comparison-data,
  .simple-comaprison-data__empty-slot {
    width: 25%;
    padding: 0.7rem 0.5rem;
    min-width: 12rem;
    font-weight: 300;
  }

  .simple-comparison-row__label {
    display: flex;
    align-items: center;
    min-width: 100px;
    flex-wrap: wrap;

    label.checkbox-label {
      display: flex;
      align-items: center;
      height: 100%;
      font-size: 14px;
      font-weight: 300;

      :deep() {
        span.checkbox {
          height: 24px;
          width: 24px;
          margin-right: 4px;
        }
      }
    }
  }

  .simple-comparison-row__label, .similar-comparison-data {
    background-color: #fff;
  }
  .simple-comparison-row__label.even,
  .similar-comparison-data.even {
    //background-color: #f8f8f8;
  }
  .simple-comaprison-data__empty-slot {
    cursor: pointer;
  }
}
.fact-value {
  display: block;
  flex-basis: 50%;
}
.fact-label {
  display: block;
  //width: 100%;
  flex: 0 0 100%; /* flex-grow, flex-shrink, flex-basis */
}
.values-to-bottom {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
</style>
